import {
  DateDisplay,
  IndicatorChip,
  JupiterTheme,
} from '@jupiterone/jupiter-web-toolkit';
import { Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import CloseIcon from 'react-feather/dist/icons/x';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    header: {
      width: '100%',
      backgroundColor: theme.palette.primary.main,
      borderTop: `solid 4px ${theme.palette.primary.dark}`,
      padding: 0,
    },
    disabledHeader: {
      backgroundColor: theme.palette.coolGrey[500],
      borderTop: `solid 4px ${theme.palette.coolGrey[700]}`,
    },
    headerTitle: {
      display: 'flex',
      padding: theme.spacing(1, 2, 0, 3),
    },
    headerTitleText: {
      color: theme.palette.common.white,
      flex: 1,
      lineHeight: 1.25,
      alignSelf: 'center',
      textTransform: 'none',
      maxWidth: 400,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingTop: theme.spacing(0.75),
    },
    headerSubText: {
      display: 'block',
      clear: 'left',
      fontWeight: 'normal',
      lineHeight: 1.3,
      marginTop: theme.spacing(0.375),
      marginBottom: theme.spacing(0.75),
      color: theme.palette.blue[100],
    },
    headerActions: {
      position: 'relative',
    },
    tabLabel: {
      color: theme.palette.common.white,
      fontSize: theme.typography.body1.fontSize,
      display: 'inline-block',
      margin: theme.spacing(0, 1),
    },
    headerTitleClose: {
      color: theme.palette.blue[100],
      width: 24,
      height: 24,
      fontSize: 24,
      margin: 6,
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.common.white,
      },
    },
    contentSection: {
      display: 'flex',
    },
    contentContainer: {
      flexGrow: 1,
    },
    deletedIndicator: {
      cursor: 'pointer',
      margin: theme.spacing(0, 2),
    },
  })
);

interface Props {
  title: JSX.Element | string;
  subtitle: JSX.Element | string;
  content?: JSX.Element | string;
  iconActions?: JSX.Element | string;
  children?: JSX.Element;
  onClose?: VoidFunction;
  deleted?: boolean;
  deletedOn?: string;
}

const EntityDrawerHeader = ({
  title,
  subtitle,
  children,
  onClose,
  content,
  iconActions,
  deleted,
  deletedOn,
}: Props) => {
  const classes = useStyles();
  return (
    <div className={clsx(classes.header, deleted && classes.disabledHeader)}>
      <div className={classes.headerTitle}>
        <Typography variant="subtitle1" className={classes.headerTitleText}>
          {title}
          <sub className={classes.headerSubText}>{subtitle}</sub>
        </Typography>
        {iconActions}
        {onClose && (
          <CloseIcon
            className={classes.headerTitleClose}
            aria-label="close"
            onClick={onClose}
          />
        )}
      </div>
      <div className={classes.contentSection}>
        <div className={classes.contentContainer}>{content}</div>
        {deleted && (
          <Tooltip
            placement="left"
            title={
              deletedOn ? (
                <span>
                  {'Deleted '}
                  <DateDisplay
                    date={deletedOn}
                    distanceFormat="toNowStrict"
                    distanceOptions={{
                      includeSeconds: false,
                      addSuffix: true,
                    }}
                  />
                </span>
              ) : (
                "Can't find the deletion date"
              )
            }
          >
            <IndicatorChip
              color="blue"
              label="Deleted"
              className={classes.deletedIndicator}
            />
          </Tooltip>
        )}
      </div>
      <div className={classes.headerActions}>{children}</div>
    </div>
  );
};

export default EntityDrawerHeader;
